import { useEffect, useState } from "react";
import { api, MEDIA } from "../lib/api";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import BeforeAfter from "../components/BeforeAfter";
import { toast } from "sonner";

export default function Landing() {
  const [cfg, setCfg] = useState(null);
  const [size, setSize] = useState("medium");
  const [addons, setAddons] = useState([]);
  const [estimate, setEstimate] = useState(150);

  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    cemetery_name: "",
    cemetery_address: "",
    notes: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [submittedQuote, setSubmittedQuote] = useState(null);

  useEffect(() => {
    api.get("/config").then((r) => setCfg(r.data));
  }, []);

  useEffect(() => {
    if (!cfg) return;
    const base = cfg.size_tiers[size]?.price || 0;
    const addonSum = addons.reduce((s, a) => s + (cfg.addons[a]?.price || 0), 0);
    setEstimate(base + addonSum);
  }, [size, addons, cfg]);

  const toggleAddon = (key) => {
    setAddons((prev) => (prev.includes(key) ? prev.filter((a) => a !== key) : [...prev, key]));
  };

  const submitQuote = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const { data } = await api.post("/quotes/request", {
        ...form,
        stone_size: size,
        addons,
      });
      setSubmittedQuote(data);
      toast.success("Quote request received — we'll be in touch within 24 hours.");
      setForm({ name: "", email: "", phone: "", cemetery_name: "", cemetery_address: "", notes: "" });
    } catch (err) {
      toast.error("Could not submit. Please try again or call us.");
    } finally {
      setSubmitting(false);
    }
  };

  if (!cfg) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F7F6F2]">
        <p className="overline">Loading…</p>
      </div>
    );
  }

  return (
    <div className="bg-[#F7F6F2]" data-testid="landing-page">
      <Navbar businessName={cfg.business_name} />

      {/* HERO */}
      <section className="relative min-h-[92vh] flex items-end pb-24 pt-32 overflow-hidden" data-testid="hero-section">
        <div className="absolute inset-0">
          <img src={MEDIA.hero} alt="Historic cemetery at sunrise" className="w-full h-full object-cover" />
          {/* Subtle vignette: keep image vivid, but ensure text legibility on left */}
          <div className="absolute inset-0 bg-gradient-to-r from-[#F7F6F2]/85 via-[#F7F6F2]/30 to-transparent" />
          <div className="absolute inset-x-0 bottom-0 h-40 bg-gradient-to-b from-transparent to-[#F7F6F2]" />
        </div>
        <div className="container-x relative z-10">
          <div className="relative fade-up max-w-3xl">
            {/* Soft white plate — corners fade into the cemetery image */}
            <div
              className="absolute -inset-4 bg-[#F7F6F2]"
              style={{
                WebkitMaskImage:
                  "radial-gradient(ellipse 78% 82% at 50% 50%, #000 72%, transparent 100%)",
                maskImage:
                  "radial-gradient(ellipse 78% 82% at 50% 50%, #000 72%, transparent 100%)",
              }}
              aria-hidden
            />
            <div className="relative px-8 py-12 md:px-14 md:py-16">
              <h1 className="font-serif-heading text-6xl md:text-8xl leading-[1.02] tracking-tight text-[#232323] mb-6">
                Stone Shepherd
              </h1>
              <div className="inline-flex items-center gap-2 bg-white px-4 py-2 mb-5 border border-[#DCD9CE] shadow-sm">
                <span className="w-1.5 h-1.5 rounded-full bg-[#98604A]" />
                <span className="text-[11px] font-semibold uppercase tracking-[0.2em] text-[#2D3F32]">
                  Memorial Restoration · {cfg.business_location}
                </span>
              </div>
              <p className="font-serif-heading italic text-2xl md:text-3xl text-[#3a3a3a] mb-8">
                Honor those who have found rest.
              </p>
              <p className="text-lg md:text-xl text-[#232323] max-w-2xl mb-10 leading-relaxed">
                Careful, non-destructive cleaning of historic and modern headstones using
                D/2 Biological Solution — the same gentle method approved by the National
                Park Service and Arlington National Cemetery.
              </p>
              <div className="flex flex-wrap gap-4">
                <a href="#quote" className="btn-primary" data-testid="hero-cta-quote">
                  <svg width="15" height="15" viewBox="0 0 32 32" fill="none" aria-hidden>
                    <path d="M16 3 C 10 3, 6 7, 6 14 L 6 28 L 26 28 L 26 14 C 26 7, 22 3, 16 3 Z" stroke="currentColor" strokeWidth="1.8" fill="none" />
                    <line x1="16" y1="10" x2="16" y2="20" stroke="currentColor" strokeWidth="1.4" />
                    <line x1="11" y1="14" x2="21" y2="14" stroke="currentColor" strokeWidth="1.4" />
                  </svg>
                  Request a free quote
                </a>
                <a href="#gallery" className="btn-secondary" data-testid="hero-cta-work">
                  See our work
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* MISSION */}
      <section id="mission" className="section-pad" data-testid="mission-section">
        <div className="container-x grid md:grid-cols-12 gap-12 items-start">
          <div className="md:col-span-4">
            <p className="overline mb-4 text-[#98604A]">Our Mission</p>
            <span className="divider-line" />
            {/* Business card mark */}
            <div className="mt-10 relative">
              <div className="absolute inset-0 bg-[#98604A]/10 translate-x-2 translate-y-2" aria-hidden />
              <div className="relative bg-white border border-[#DCD9CE] p-5 shadow-sm">
                <div className="overflow-hidden">
                  <img
                    src={MEDIA.card}
                    alt="Stone Shepherd — Headstone Cleaner — Athens, GA"
                    className="w-full h-auto block"
                    style={{ transform: "scale(1.05)", transformOrigin: "center" }}
                    data-testid="brand-card-image"
                  />
                </div>
              </div>
              <p className="mt-4 text-xs text-[#5C5C5C] italic font-serif-heading">
                — Our mark, carried into every cemetery we visit.
              </p>
            </div>
          </div>
          <div className="md:col-span-8">
            <h2 className="font-serif-heading text-4xl md:text-5xl leading-tight mb-8 text-[#232323]">
              Every stone tells a story.
              <br />
              <span className="text-[#5C5C5C] italic">We make sure it can still be read.</span>
            </h2>
            <div className="grid md:grid-cols-2 gap-10 text-[#3a3a3a] leading-relaxed">
              <p>
                Decades of weather, biological growth, and neglect can render a marker
                illegible — severing a family's link to their history. We restore
                that link with patient hands and methods proven to preserve, not damage.
              </p>
              <p>
                We never use bleach, wire brushes, or pressure washers. Only soft water,
                soft bristle, and time. Whether for a single ancestor's marker or an
                entire historic plot, we treat every stone with the reverence it deserves.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* GALLERY / BEFORE-AFTER */}
      <section id="gallery" className="bg-[#EBE8E0] section-pad" data-testid="gallery-section">
        <div className="container-x">
          <div className="grid md:grid-cols-12 gap-12 mb-12 items-end">
            <div className="md:col-span-7">
              <p className="overline mb-4 text-[#98604A]">Our Work</p>
              <h2 className="font-serif-heading text-4xl md:text-5xl leading-tight text-[#232323]">
                Drag to reveal the difference.
              </h2>
            </div>
            <div className="md:col-span-5">
              <p className="text-[#5C5C5C] leading-relaxed">
                Below is a family marker for Elisha C. (1859–1942) and Sarah E. (1862–1931),
                cleaned with D/2 Biological Solution. No abrasives. No pressure. Just careful work.
              </p>
            </div>
          </div>
          <div className="max-w-4xl mx-auto shadow-lg">
            <BeforeAfter before={MEDIA.before} after={MEDIA.after} />
          </div>
        </div>
      </section>

      {/* PROCESS */}
      <section id="process" className="section-pad" data-testid="process-section">
        <div className="container-x grid md:grid-cols-2 gap-16 items-center">
          <div>
            <p className="overline mb-4 text-[#98604A]">The Process</p>
            <h2 className="font-serif-heading text-4xl md:text-5xl leading-tight mb-8 text-[#232323]">
              Patient, methodical, reverent.
            </h2>
            <div className="space-y-8">
              {[
                {
                  n: "01",
                  t: "Assessment",
                  d: "Every stone starts with a walkthrough — we note the material, biological growth, fractures, and how the marker sits in the ground. No two headstones are alike, and understanding what you're dealing with is half the work.",
                },
                {
                  n: "02",
                  t: "D/2 Biological Treatment",
                  d: "We apply D/2 — a non-ionic, non-acidic cleaner safe for marble, granite, slate, and bronze. It works gently over days, not minutes.",
                },
                {
                  n: "03",
                  t: "Preservation",
                  d: "Soft rinse, light agitation with natural-bristle brushes, and documentation photos sent to the family.",
                },
              ].map((s) => (
                <div key={s.n} className="flex gap-6">
                  <div className="font-serif-heading text-3xl text-[#98604A] w-12 flex-shrink-0">{s.n}</div>
                  <div>
                    <h3 className="font-serif-heading text-2xl mb-2 text-[#232323]">{s.t}</h3>
                    <p className="text-[#5C5C5C] leading-relaxed">{s.d}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="relative">
            <img src={MEDIA.process} alt="Hands cleaning a historic stone" className="w-full h-auto object-cover" />
            <div className="absolute -bottom-6 -left-6 bg-[#3F5746] text-white p-6 md:p-8 max-w-xs hidden md:block">
              <p className="font-serif-heading text-2xl leading-tight">
                "The kindest cleaner I've ever used on marble."
              </p>
              <p className="overline mt-4 text-[#DCD9CE]">— National Park Service</p>
            </div>
          </div>
        </div>
      </section>

      {/* ESTIMATE CALCULATOR */}
      <section id="estimate" className="bg-[#3F5746] text-white section-pad" data-testid="estimate-section">
        <div className="container-x grid md:grid-cols-12 gap-12 items-start">
          <div className="md:col-span-5">
            <p className="overline mb-4 text-[#DCD9CE]">Pricing</p>
            <h2 className="font-serif-heading text-4xl md:text-5xl leading-tight mb-6">
              A clear estimate, before we ever lift a brush.
            </h2>
            <p className="text-[#DCD9CE] leading-relaxed">
              Prices below are a starting point. Final quotes account for stone
              condition, accessibility, and number of stones. Heavily soiled or pre-1900
              stones may require additional treatments.
            </p>
          </div>
          <div className="md:col-span-7 bg-[#F7F6F2] text-[#232323] p-8 md:p-12">
            <p className="overline mb-6 text-[#98604A]">Quick Estimate</p>
            <div className="mb-8">
              <label className="block text-sm font-medium mb-3 uppercase tracking-wider">Stone Size</label>
              <div className="grid sm:grid-cols-3 gap-3" data-testid="size-selector">
                {Object.entries(cfg.size_tiers).map(([k, v]) => (
                  <button
                    key={k}
                    type="button"
                    onClick={() => setSize(k)}
                    data-testid={`size-option-${k}`}
                    className={`text-left p-4 border transition ${
                      size === k
                        ? "border-[#3F5746] bg-[#3F5746] text-white"
                        : "border-[#DCD9CE] bg-white hover:border-[#3F5746]"
                    }`}
                  >
                    <div className="font-serif-heading text-2xl mb-1">${v.price}{k === "large" ? "+" : ""}</div>
                    <div className="text-xs leading-tight opacity-80">{v.label}</div>
                  </button>
                ))}
              </div>
            </div>

            <div className="flex items-end justify-between pt-6 border-t border-[#DCD9CE]">
              <div>
                <p className="overline text-[#98604A] mb-1">Estimated</p>
                <p className="font-serif-heading text-5xl text-[#232323]" data-testid="estimate-display">
                  ${estimate.toFixed(0)}
                </p>
              </div>
              <a href="#quote" className="btn-primary" data-testid="estimate-cta">
                <svg width="15" height="15" viewBox="0 0 32 32" fill="none" aria-hidden>
                  <path d="M16 3 C 10 3, 6 7, 6 14 L 6 28 L 26 28 L 26 14 C 26 7, 22 3, 16 3 Z" stroke="currentColor" strokeWidth="1.8" fill="none" />
                  <line x1="16" y1="10" x2="16" y2="20" stroke="currentColor" strokeWidth="1.4" />
                  <line x1="11" y1="14" x2="21" y2="14" stroke="currentColor" strokeWidth="1.4" />
                </svg>
                Request quote
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* QUOTE FORM */}
      <section id="quote" className="section-pad" data-testid="quote-section">
        <div className="container-x grid md:grid-cols-12 gap-12">
          <div className="md:col-span-4">
            <p className="overline mb-4 text-[#98604A]">Request a Quote</p>
            <h2 className="font-serif-heading text-4xl md:text-5xl leading-tight mb-6 text-[#232323]">
              Tell us about the stone.
            </h2>
            <p className="text-[#5C5C5C] leading-relaxed mb-6">
              We'll review your request and email a firm quote — typically within
              24 hours. Payment is handled securely online once you approve the quote.
            </p>
            <div className="text-sm text-[#5C5C5C] space-y-2">
              <p><strong className="text-[#232323]">Email:</strong> {cfg.business_email}</p>
              <p><strong className="text-[#232323]">Phone:</strong> {cfg.business_phone}</p>
              <p><strong className="text-[#232323]">Service Area:</strong> {cfg.business_location}</p>
            </div>
          </div>

          <div className="md:col-span-8">
            {submittedQuote ? (
              <div className="bg-white border border-[#DCD9CE] p-10" data-testid="quote-success">
                <p className="overline mb-4 text-[#3F5746]">Received</p>
                <h3 className="font-serif-heading text-3xl mb-4 text-[#232323]">
                  Thank you, {submittedQuote.name.split(" ")[0]}.
                </h3>
                <p className="text-[#5C5C5C] leading-relaxed mb-6">
                  Your request has been logged. We'll review the details and follow up
                  via email at <strong>{submittedQuote.email}</strong> with a firm quote and a secure
                  payment link.
                </p>
                <div className="bg-[#EBE8E0] p-6 mb-6">
                  <p className="overline mb-2 text-[#98604A]">Your Reference</p>
                  <p className="font-mono text-sm break-all">{submittedQuote.id}</p>
                  <p className="overline mt-4 mb-1 text-[#98604A]">Preliminary Estimate</p>
                  <p className="font-serif-heading text-3xl">${submittedQuote.estimated_price.toFixed(0)}</p>
                </div>
                <button
                  type="button"
                  onClick={() => setSubmittedQuote(null)}
                  className="btn-secondary"
                  data-testid="quote-new-button"
                >
                  Submit another
                </button>
              </div>
            ) : (
              <form onSubmit={submitQuote} className="bg-white border border-[#DCD9CE] p-8 md:p-10 space-y-5" data-testid="quote-form">
                <div className="grid sm:grid-cols-2 gap-5">
                  <div>
                    <label className="overline mb-2 block">Full name</label>
                    <input
                      required
                      className="input-field"
                      value={form.name}
                      onChange={(e) => setForm({ ...form, name: e.target.value })}
                      data-testid="quote-input-name"
                    />
                  </div>
                  <div>
                    <label className="overline mb-2 block">Email</label>
                    <input
                      required
                      type="email"
                      className="input-field"
                      value={form.email}
                      onChange={(e) => setForm({ ...form, email: e.target.value })}
                      data-testid="quote-input-email"
                    />
                  </div>
                </div>

                <div className="grid sm:grid-cols-2 gap-5">
                  <div>
                    <label className="overline mb-2 block">Phone</label>
                    <input
                      required
                      className="input-field"
                      value={form.phone}
                      onChange={(e) => setForm({ ...form, phone: e.target.value })}
                      data-testid="quote-input-phone"
                    />
                  </div>
                  <div>
                    <label className="overline mb-2 block">Cemetery name</label>
                    <input
                      required
                      className="input-field"
                      value={form.cemetery_name}
                      onChange={(e) => setForm({ ...form, cemetery_name: e.target.value })}
                      data-testid="quote-input-cemetery"
                    />
                  </div>
                </div>

                <div>
                  <label className="overline mb-2 block">Cemetery address / location notes</label>
                  <input
                    className="input-field"
                    value={form.cemetery_address}
                    onChange={(e) => setForm({ ...form, cemetery_address: e.target.value })}
                    data-testid="quote-input-address"
                  />
                </div>

                <div className="grid sm:grid-cols-2 gap-5 pt-3 border-t border-[#DCD9CE]">
                  <div>
                    <label className="overline mb-2 block">Stone size</label>
                    <select
                      className="input-field appearance-none"
                      value={size}
                      onChange={(e) => setSize(e.target.value)}
                      data-testid="quote-input-size"
                    >
                      {Object.entries(cfg.size_tiers).map(([k, v]) => (
                        <option key={k} value={k}>{v.label}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="overline mb-2 block">Estimate</label>
                    <div className="input-field bg-[#EBE8E0]/60 font-serif-heading text-2xl flex items-center">
                      ${estimate.toFixed(0)}
                    </div>
                  </div>
                </div>

                <div>
                  <label className="overline mb-2 block">Notes (inscription, age of stone, special concerns)</label>
                  <textarea
                    rows={4}
                    className="input-field resize-none"
                    value={form.notes}
                    onChange={(e) => setForm({ ...form, notes: e.target.value })}
                    data-testid="quote-input-notes"
                  />
                </div>

                <button
                  type="submit"
                  disabled={submitting}
                  className="btn-primary"
                  data-testid="quote-submit-button"
                >
                  {submitting ? "Sending…" : "Send request"}
                </button>
              </form>
            )}
          </div>
        </div>
      </section>

      <Footer
        businessName={cfg.business_name}
        email={cfg.business_email}
        phone={cfg.business_phone}
        location={cfg.business_location}
      />
    </div>
  );
}
